/**
 * PALETTE DE SÉRIES — les couleurs qui portent une IDENTITÉ dans un graphique (parts de règlement,
 * répartition par vendeur), et rien d'autre.
 *
 * ⚠️ POURQUOI ELLE NE RÉUTILISE PAS LES ACCENTS SÉMANTIQUES. La charte assigne déjà un sens à ses
 * accents — `modal.ts` l'écrit : « import = blue · gamme = violet · réglage à risque = amber ·
 * destructif = rose ». Peindre une part de chiffre d'affaires en ambre apprendrait à l'exploitant que
 * l'ambre ne veut rien dire, et le jour où un vrai avertissement s'affiche il ne le verra plus. Une
 * couleur de statut est réservée.
 *
 * ⚠️ L'ORDRE EST FIXE ET NE TOURNE JAMAIS. Une couleur suit l'ENTITÉ, pas son rang : si un filtre
 * retire un moyen de paiement, les survivants gardent leur teinte. Repeindre la série au moindre
 * changement de tri fait relire toute la légende à chaque fois. Au-delà de six séries, on ne fabrique
 * pas une septième teinte : on replie dans « Autres », ou on facette.
 *
 * ⚠️ LE MODE SOMBRE A SES PROPRES PAS, il n'est pas une inversion. La seule différence tient au vert de
 * marque : `teal-500` est trop clair pour la bande de clarté du sombre, `teal-600` y entre. Les cinq
 * autres teintes passent les deux modes telles quelles.
 *
 * ⚠️ CETTE PALETTE A ÉTÉ VALIDÉE, PAS ESTIMÉE (2026-08-17, `validate_palette.js`) :
 *   clair  — bande de clarté 0,43–0,77 ✅ · plancher de chroma ✅ · séparation daltonienne ✅ ·
 *            plancher vision normale ΔE 25,6 (seuil 15) ✅ · contraste < 3:1 ⚠️
 *   sombre — bande 0,48–0,67 ✅ · séparation ✅ · vision normale ✅ · contraste ≥ 3:1 ✅
 * L'avertissement de contraste en clair est LEVÉ par la lecture directe : tout graphique qui emploie
 * cette palette porte des étiquettes lisibles (nom + valeur en toutes lettres à côté de la pastille).
 * Une part de camembert sans sa légende ne serait pas conforme — c'est une contrainte d'usage, pas un
 * détail de style. De même, la séparation tritan du sombre (ΔE 7,9) n'est acceptable QUE parce que les
 * segments sont séparés par un vide de 2 px : la forme double la couleur.
 */

import { Z_LAYERS } from './z';
import { TIP_SKIN } from './tooltip';

/** Les six teintes, dans l'ordre d'attribution. La première est celle de la marque. */
export const SERIES_LIGHT = ['#16a585', '#4c6ef5', '#c2255c', '#66a80f', '#9c36b5', '#1098ad'] as const;
export const SERIES_DARK = ['#16a585', '#4c6ef5', '#c2255c', '#66a80f', '#9c36b5', '#1098ad'] as const;

/** Nombre de séries distinctes avant repli sur « Autres ». */
export const SERIES_MAX = SERIES_LIGHT.length;

/**
 * Géométrie commune des graphiques.
 * ⚠️ `gap` n'est pas cosmétique : c'est le SECOND ENCODAGE qui rend deux parts voisines distinguables
 * quand la couleur seule ne suffit pas (daltonisme, impression, contraste forcé).
 */
export const CHART = {
  /** Vide entre deux remplissages adjacents (segments de donut). ⚠️ L'histogramme a le sien
   *  (`BARS.pairGap`) : il sépare les deux barres d'une PAIRE, ce qui n'est pas le même vide. */
  gap: 2,
  /**
   * Épaisseur d'un tracé (sparkline, courbe), relevée sur la maquette.
   * ⚠️ ELLE NE VAUT QUELQUE CHOSE QU'AVEC `vector-effect: non-scaling-stroke` : une sparkline est
   * étirée (`preserveAspectRatio="none"`), et sans cela le trait est épaissi verticalement autant que
   * le dessin — il devient un ruban en haut et un cheveu sur les côtés.
   */
  lineWidth: 1.6,
  /** Opacité de l'aire sous la courbe — un aplat, pas un dégradé (relevé maquette). */
  areaOpacity: 0.12,
  /** Hauteur d'une sparkline de tuile. */
  sparkHeight: 30,
  /** Épaisseur d'un anneau (donut, jauge), en unités du `viewBox` 36 de la maquette. */
  ringWidth: 5,
  /** Rayon de l'anneau dans ce même `viewBox` — repris de la maquette au chiffre près. */
  ringRadius: 15.5,
} as const;

/**
 * L'ANNEAU DE RÉPARTITION — relevé au chiffre près sur les cartes « vendeurs » et « paiements ».
 *
 * ⚠️ RIEN À VOIR AVEC LA JAUGE, qui vit dans un carré de 36 : celle-ci compare une valeur à 100 %,
 * l'anneau compare des parts entre elles. Les confondre — c'était le cas — donnait un anneau fin de
 * jauge là où la maquette dessine un large disque de répartition.
 *
 * ⚠️ LE VIDE DE 1,6 ENTRE LES PARTS EST STRUCTUREL : c'est le second encodage qui rend deux parts
 * voisines distinguables en daltonisme, à l'impression et en contraste forcé. La palette de séries
 * n'est conforme qu'avec lui.
 *
 * ⚠️ LE SURVOL AGRANDIT LA PART VISÉE (1,055) ET ESTOMPE LES AUTRES (0,45), au lieu de seulement
 * changer sa teinte : sur un anneau, un changement de couleur se confond avec la part voisine — le
 * relief, lui, ne se confond avec rien.
 */
/**
 * LES TEINTES DES PARTS D'UN ANNEAU — celles de la maquette, sur décision de l'utilisateur.
 *
 * ⚠️ ELLES NE PASSENT PAS LE VALIDATEUR, et le dossier doit le dire plutôt que de l'oublier :
 *   · bleu ↔ violet : **ΔE 0,3 en deutéranopie** — la même couleur pour ~8 % des hommes ;
 *   · menthe ↔ menthe claire : **ΔE 7,8 en vision normale**, sous le plancher de 15 ;
 *   · `teal-400` sort de la bande de clarté sur fond clair.
 * Trois variantes intermédiaires ont été éprouvées (menthe foncée à la place de la claire) : toutes
 * butent sur le plancher de chroma — une menthe assez sombre pour se séparer vire au gris.
 *
 * ⚠️ CE QUI REND L'ANNEAU LISIBLE MALGRÉ ÇA, et qui n'est donc pas décoratif : chaque part porte son
 * nom EN TOUTES LETTRES dans la légende juste dessous, avec sa pastille ; le vide de 1,6 sépare deux
 * parts voisines ; le survol en agrandit une et estompe les autres. L'identité n'est jamais portée par
 * la seule couleur — retirer la légende rendrait l'anneau non conforme.
 *
 * ⚠️ `SERIES_LIGHT` reste la palette VALIDÉE du dépôt pour tout nouveau graphique : celle-ci est
 * l'exception d'un écran, pas la nouvelle règle.
 */
export const DONUT_SERIES = ['teal-500', 'teal-300', 'blue', 'violet', 'amber', 'teal-700'] as const;

/**
 * ⚠️ LES SIX SONT TOUTES DES TEINTES DE DONNÉES DE LA MAQUETTE : les quatre de l'anneau, plus l'ambre
 * et la menthe foncée que le tableau des articles emploie pour ses familles. On ÉTEND le vocabulaire
 * existant plutôt que d'en inventer un — et le rose reste dehors : la charte le réserve au destructif,
 * et une part de chiffre d'affaires peinte en rose apprendrait que le rose ne veut rien dire.
 *
 * ⚠️ LA QUEUE N'EST PAS UNE COULEUR DE PLUS. Au-delà de la sixième, la teinte devient NEUTRE — parce
 * qu'au-delà, ce n'est plus une catégorie mais un reste (`foldTail`). Le défaut d'avant plafonnait sur
 * la dernière teinte : trois vendeurs sur quatre ressortaient violets, et l'anneau devenait illisible
 * sans qu'aucune erreur ne le signale.
 */
export const DONUT_TAIL = 'ink-300';

/**
 * SOUS CETTE PART, UNE TRANCHE N'EST PLUS LISIBLE — elle rejoint « Autres ».
 *
 * ⚠️ CE N'EST PAS UN CHOIX DE GOÛT, C'EST DE LA GÉOMÉTRIE. 3 % d'un cercle font 10,8° ; l'anneau
 * pose déjà 2 px de vide entre deux tranches (le second encodage qui les sépare sans couleur), si
 * bien qu'au-dessous de ce seuil l'arc est plus mince que son propre séparateur. On dessine alors un
 * trait que personne ne peut viser, cliquer, ni comparer — et sa LÉGENDE, elle, occupe une ligne
 * entière : dix vendeurs à 1 % rendent la carte illisible pour une information qui ne se voit pas.
 *
 * ⚠️ LE TOTAL RESTE JUSTE : les parts repliées sont SOMMÉES dans « Autres », jamais retirées. Un
 * anneau dont les tranches n'additionnent plus à 100 % serait un mensonge, pas une simplification.
 */
export const DONUT_MIN_SHARE_BP = 300;

/** La teinte d'une part par son RANG. Au-delà de la palette : l'encre neutre du RESTE. */
export function donutVar(index: number): string {
  const nom = index < DONUT_SERIES.length ? DONUT_SERIES[index] : DONUT_TAIL;
  return `var(--${nom})`;
}

/** ⚠️ Le donut a SON rayon (52) : celui de la jauge (15,5) donnait un anneau six fois trop fin. */
export const DONUT_CIRCUMFERENCE = 2 * Math.PI * 52;

export const DONUT = {
  /** Repère interne du tracé ; le rendu est mis à l'échelle par `size`. */
  box: 140,
  radius: 52,
  strokeWidth: 20,
  /** Taille rendue, en pixels (la maquette dessine 148 dans un repère de 140). */
  size: 148,
  /** Vide entre deux parts, en unités du repère. */
  gap: 1.6,
  hoverScale: 1.055,
  dimOpacity: 0.45,
  transitionMs: 160,
} as const;

/**
 * L'HISTOGRAMME DU TABLEAU DE BORD — relevé au chiffre près sur la maquette du back-office.
 *
 * ⚠️ LA BARRE A UNE LARGEUR MAXIMALE, ET C'EST LE POINT. Sans plafond, une période de six intervalles
 * étale six pavés de 90 px : ce ne sont plus des barres, c'est un aplat. La paire occupe donc 62 % de
 * son créneau — le reste est le blanc qui sépare deux intervalles et permet de les compter.
 *
 * ⚠️ LA MARGE HAUTE (12 %) N'EST PAS DÉCORATIVE : sans elle, la plus grande barre touche le bord du
 * cadre, et rien ne dit si elle est au maximum ou si elle est coupée.
 *
 * ⚠️ TROIS LIGNES DE REPÈRE AUX QUARTS, en encre très pâle : un histogramme sans repère horizontal ne
 * permet pas de comparer deux barres éloignées — l'œil suit le bord du cadre, qui est trop loin.
 */
export const BARS = {
  height: 190,
  /** Largeur MAXIMALE d'une barre, en pixels. */
  maxBarWidth: 22,
  /** Vide entre les deux barres d'une même paire. */
  pairGap: 3,
  /** Part du créneau qu'occupe la paire. */
  slotFill: 0.62,
  /** Plancher : en dessous, une barre cesse d'être visible. */
  minPairWidth: 8,
  /** Facteur d'échelle appliqué au maximum, pour dégager le haut du cadre. */
  headroom: 1.12,
  guides: 3,
  guideOpacity: 0.35,
  barRadius: 2,
  /**
   * Opacités de la RÉFÉRENCE (l'an dernier) et du SUJET (la période affichée), au repos et au survol.
   * ⚠️ La référence est en encre NEUTRE et non dans une seconde teinte de série : ce ne sont pas deux
   * catégories de même rang, c'est un sujet et son point de comparaison. Deux couleurs vives les
   * mettraient à égalité, et l'œil ne saurait plus laquelle est l'année qu'on regarde.
   */
  refOpacity: 0.42,
  refOpacityHover: 0.62,
  nowOpacity: 0.88,
  nowOpacityHover: 1,
} as const;

/**
 * Où se posent les barres d'un intervalle, en pixels du `viewBox`.
 * ⚠️ SORTIE DU RENDU parce qu'elle se trompe en silence : une paire plus large que son créneau fait
 * se chevaucher deux intervalles, et une largeur négative (créneau minuscule) fait disparaître les
 * barres sans erreur. Les deux sont épinglées par des tests.
 */
export function barLayout(width: number, count: number): { slot: number; pairWidth: number; barWidth: number } {
  if (count <= 0 || width <= 0) return { slot: 0, pairWidth: 0, barWidth: 0 };
  const slot = width / count;
  const pairWidth = Math.min(
    BARS.maxBarWidth * 2 + BARS.pairGap,
    Math.max(BARS.minPairWidth, slot * BARS.slotFill),
  );
  return { slot, pairWidth, barWidth: Math.max(1, (pairWidth - BARS.pairGap) / 2) };
}

/**
 * L'INFOBULLE QUI SUIT LE CURSEUR — relevé de la maquette (`tipStyle`).
 * ⚠️ ELLE SE DÉCALE DU CURSEUR (+14 en x, −8 en y) et ne se pose jamais dessous : posée sous le
 * pointeur, elle serait masquée par la main et le curseur lui-même.
 * ⚠️ `edgeReserve` est la place gardée à droite de l'écran : sans elle, la bulle sort de la fenêtre
 * dès qu'on survole les derniers intervalles — ceux de la fin de période, qu'on regarde le plus.
 */
export const CHART_TIP = {
  // ⚠️ LA PEAU EST REPRISE, PAS RECOPIÉE : fond, rayon, respiration et échelle de texte viennent de
  // `TIP_SKIN`, que l'infobulle ancrée partage. Deux jeux de valeurs jumelles auraient divergé à la
  // première retouche — et personne ne voit jamais ces deux bulles côte à côte pour s'en apercevoir.
  ...TIP_SKIN,
  offsetX: 14,
  offsetY: -8,
  edgeReserve: 190,
  shadow: '0 12px 32px -8px rgba(0,0,0,.4)',
  z: Z_LAYERS.chartTip,
} as const;

/** Abscisse du bord gauche de la paire : elle est CENTRÉE dans son créneau, jamais collée à gauche. */
export const barX = (index: number, slot: number, pairWidth: number): number =>
  index * slot + (slot - pairWidth) / 2;

/** La couleur d'une série par son RANG (0-indexé), sans jamais cycler au-delà du dernier. */
export function seriesVar(index: number): string {
  return `var(--series-${Math.min(index, SERIES_MAX - 1) + 1})`;
}

// ── GÉOMÉTRIE PURE ────────────────────────────────────────────────────────────────────────────────
// ⚠️ Ici et non dans le composant : c'est le calcul qui peut se tromper en silence (une part qui
// déborde, un vide qui mange une petite valeur, une jauge qui dépasse le tour). Sorti du rendu, il
// devient vérifiable — le rendu, lui, n'est plus que du placement.

/** Circonférence de l'anneau, dans les unités du `viewBox` 36 de la maquette. */
export const RING_CIRCUMFERENCE = 2 * Math.PI * CHART.ringRadius;

export interface RingSegment {
  /** Longueur du trait coloré. */
  dash: number;
  /** Décalage négatif appliqué au départ du segment. */
  offset: number;
}

/**
 * Découpe un anneau en parts proportionnelles, séparées d'un vide.
 *
 * @param values valeurs brutes ; les négatives comptent pour zéro (un remboursement ne « prend » pas
 *               une part de camembert — il n'a pas de surface).
 * @param gapVb  vide entre deux parts, EN UNITÉS DU VIEWBOX (le composant convertit ses pixels).
 *
 * ⚠️ LE VIDE EST RETIRÉ DU TRAIT, PAS AJOUTÉ APRÈS : sinon la somme des parts dépasserait le tour et la
 * dernière recouvrirait la première. Et il ne peut pas rendre un trait négatif — une part minuscule
 * devient un point, jamais un trait à l'envers.
 */
export function ringSegments(values: number[], gapVb: number, circumference = RING_CIRCUMFERENCE): RingSegment[] {
  const positives = values.map((v) => Math.max(0, v));
  const total = positives.reduce((s, v) => s + v, 0);
  if (total <= 0) return [];
  let debut = 0;
  return positives.map((v) => {
    const part = (v / total) * circumference;
    const seg = { dash: Math.max(0, part - gapVb), offset: -debut };
    debut += part;
    return seg;
  });
}

/**
 * Longueur de l'arc d'une jauge, depuis une part en points de base.
 * ⚠️ BORNÉE À UN TOUR : un taux au-delà de 100 % (objectif dépassé) doit remplir l'anneau, jamais
 * repartir pour un second tour — la jauge se relirait comme un faible pourcentage.
 */
/**
 * CE QU'UNE JAUGE A LE DROIT DE DIRE D'UNE VALEUR.
 *
 * ⚠️ UN POURCENTAGE N'EST PAS UNE NOTE. « 37 % de clients identifiés » n'est ni bon ni mauvais en
 * soi : c'est excellent pour un commerce de passage, faible pour une boutique à programme de
 * fidélité. Peindre l'anneau en orange sous 50 % annoncerait un échec sur un OBJECTIF QUE PERSONNE
 * N'A FIXÉ — c'est la même faute que d'inventer un nombre (§23.0), en pire : une couleur se lit sans
 * être lue, et elle porte un jugement.
 *
 * ⚠️ LA COULEUR NE DEVIENT UN SIGNAL QUE FACE À UN OBJECTIF. Tant qu'aucun n'est posé, l'anneau porte
 * la couleur de MARQUE : il montre une proportion, il ne la juge pas. Le jour où un objectif existe
 * (réglage de magasin, seuil produit), la même fonction le rend lisible sans qu'aucun écran ne
 * réinvente ses seuils.
 *
 * ⚠️ LE SEUIL D'ALERTE EST RELATIF À L'OBJECTIF, jamais absolu : « à 80 % de la cible » veut dire la
 * même chose pour une cible à 20 % et pour une cible à 90 %. Un seuil absolu, lui, dirait « presque
 * atteint » à 45 % d'un objectif de 90 et « très en retard » à 45 % d'un objectif de 50.
 */
export type GaugeTone = 'brand' | 'good' | 'fair' | 'warn' | 'bad' | 'unmeasured';

/**
 * LES PALIERS D'UN INDICATEUR — deux bornes, trois bandes, en points de base.
 *
 * ⚠️ CE JETON NE CONTIENT AUCUN NOMBRE, ET C'EST VOULU. Les valeurs sont des règles MÉTIER (« sous
 * 5 % de transformation, c'est catastrophique ») : elles vivent dans `@orgazone/core-analytics`, avec
 * les définitions d'indicateurs, pas dans un paquet de design. Ici on ne définit que la FORME et la
 * façon de la lire — sans quoi chaque écran réinventerait ses comparaisons.
 */
export interface GaugeThresholds {
  /** Strictement en dessous : `bad`. */
  warnFromBp: number;
  /** À partir d'ici, la bande est atteinte. Entre les deux premières bornes : `warn`. */
  goodFromBp: number;
  /**
   * LA QUATRIÈME BANDE — facultative, et elle DÉPLACE le vert au lieu de s'ajouter après lui.
   *
   * ⚠️ SANS ELLE : `bad` → `warn` → `good` (vert au-dessus de `goodFromBp`). AVEC elle :
   * `bad` → `warn` → `fair` (jaune) → `good` (vert au-dessus d'ici). Le vert reste donc TOUJOURS le
   * haut de l'échelle, et le jaune s'INSÈRE — c'est ce qui garde la rampe rouge/orange/jaune/vert
   * lisible sans légende.
   *
   * ⚠️ POURQUOI PAS UNE COULEUR AU-DESSUS DU VERT (arbitrage utilisateur 2026-09-05). C'était la
   * première proposition, et elle était mauvaise : aucune teinte ne se lit « mieux que vert », et
   * une cinquième couleur aurait fait passer la bande du milieu pour un demi-échec. Insérer plutôt
   * qu'empiler résout les deux — la solution vient de l'utilisateur, pas de moi.
   *
   * ⚠️ ET LE MOT A ÉTÉ ESSAYÉ AVANT, PUIS RETIRÉ : la tuile affichait « correct » / « exceptionnel »
   * sous ses chiffres. Verdict de l'utilisateur : « aucun texte, c'est pas bon ». Une jauge se lit
   * d'un coup d'œil ; une phrase de plus se lit, et lire n'est pas regarder.
   */
  excellentFromBp?: number;
}

/** Le jeton de couleur de chaque ton — le nom de la variable CSS, sans le `var()`. */
export const GAUGE_TONES: Record<GaugeTone, string> = {
  brand: 'teal-500',
  /** LE HAUT DE L'ÉCHELLE, toujours — quel que soit le nombre de bandes. */
  good: 'teal-500',
  /**
   * AU-DESSUS DU SEUIL, SANS ÊTRE REMARQUABLE — n'existe que si une bande le surplombe.
   * ⚠️ LE VERT RESTE LE HAUT DE L'ÉCHELLE : le jaune s'INSÈRE entre l'ambre et lui, il ne le
   * remplace pas. C'est ce qui garde la rampe rouge → orange → jaune → vert lisible sans légende,
   * là où une teinte ajoutée AU-DESSUS du vert n'aurait dit que « différent ».
   */
  fair: 'yellow',
  warn: 'amber',
  bad: 'rose',
  /** Non mesuré : gris, et l'écran doit le DIRE en toutes lettres à côté. */
  unmeasured: 'ink-300',
};

/**
 * Le ton d'une jauge. `valueBp` en points de base (10 000 = 100 %).
 *
 * ⚠️ SANS PALIERS, AUCUNE NOTE N'EST PORTÉE (`brand`) : voir l'en-tête du type. C'est le défaut, et
 * il doit le rester — un indicateur dont personne n'a défini les bandes n'a pas de bonne valeur.
 * ⚠️ DES BORNES INCOHÉRENTES NE FABRIQUENT PAS UN VERDICT : si `goodFromBp` n'est pas au-dessus de
 * `warnFromBp`, la bande du milieu n'existe pas et la lecture serait arbitraire — on rend `brand`
 * plutôt qu'une couleur tirée d'un réglage cassé.
 */
export function gaugeTone(valueBp: number | null, bands?: GaugeThresholds | null): GaugeTone {
  if (valueBp === null) return 'unmeasured';
  if (!bands || bands.warnFromBp < 0 || bands.goodFromBp <= bands.warnFromBp) return 'brand';
  // ⚠️ UNE 4ᵉ BORNE INCOHÉRENTE EST IGNORÉE, pas obéie : sous `goodFromBp`, elle inverserait l'ordre
  //    des bandes et peindrait en vert une valeur moins bonne qu'une jaune. On retombe alors sur les
  //    trois bandes, qui restent justes — plutôt que sur un verdict tiré d'un réglage cassé.
  const haut = bands.excellentFromBp !== undefined && bands.excellentFromBp > bands.goodFromBp
    ? bands.excellentFromBp : null;
  if (haut !== null) {
    if (valueBp >= haut) return 'good';
    if (valueBp >= bands.goodFromBp) return 'fair';
  } else if (valueBp >= bands.goodFromBp) return 'good';
  return valueBp >= bands.warnFromBp ? 'warn' : 'bad';
}

export function gaugeDash(valueBp: number): number {
  const part = Math.min(1, Math.max(0, valueBp / 10000));
  return part * RING_CIRCUMFERENCE;
}

/**
 * Points d'une sparkline, normalisés dans un carré de 100 × 100.
 * ⚠️ UNE SÉRIE PLATE SE TRACE AU MILIEU, pas en bas : collée à la ligne de base, elle se lirait comme
 * une chute à zéro. Une série de moins de deux points ne rend rien (une tendance a besoin d'un écart).
 */
export function sparklinePoints(values: number[]): Array<{ x: number; y: number }> {
  if (values.length < 2) return [];
  const max = Math.max(...values);
  const min = Math.min(...values);
  const etendue = max - min;
  return values.map((v, i) => ({
    x: (i / (values.length - 1)) * 100,
    y: etendue === 0 ? 50 : 100 - ((v - min) / etendue) * 100,
  }));
}
